import React from "react";
import { FiFile } from "react-icons/fi";

import * as types from "types";
import type { ScopeFile } from "services/fileApi/types/ScopeFile";
import useDownloadFile from "services/cortevaChain/hooks/useDownloadFile";
import {
  findVariantPermalink,
  getPreviewKind,
} from "components/Message/FilePreviewModal";
import styles from "../FileExplorer.module.scss";

interface FileThumbnailProps {
  file: ScopeFile;
  chatThread: types.ChatThread;
}

const fileIcon = <FiFile className={styles.thumbnailIcon} aria-hidden="true" />;

interface ImageThumbnailProps {
  fileName: string;
  chatThread: types.ChatThread;
  permalink: string;
  mimeType: string;
}

/** A downloaded image, cropped to fill the thumbnail area. */
function ImageThumbnail({
  fileName,
  chatThread,
  permalink,
  mimeType,
}: ImageThumbnailProps) {
  // The same query FilePreviewModal and the chat's inline image preview use,
  // so they share one download.
  const { data } = useDownloadFile({ chatThread, fileName, permalink });
  // Label the Blob with the MIME type that matches the file's extension. The
  // browser won't display an SVG Blob unless it is labeled "image/svg+xml".
  const imageUrl = React.useMemo(() => {
    if (data === undefined) return null;
    return URL.createObjectURL(new Blob([data], { type: mimeType }));
  }, [data, mimeType]);

  // While loading, and when the download failed.
  if (imageUrl === null) return fileIcon;

  // Safe to revoke once loaded: the <img> keeps its decoded bitmap, and
  // `imageUrl` is memoized on `data`, so no re-render hands this dead URL
  // back to the browser.
  const revokeImageUrl = () => URL.revokeObjectURL(imageUrl);
  return (
    <img
      className={styles.thumbnailImage}
      src={imageUrl}
      // The tile's name labels it.
      alt=""
      onLoad={revokeImageUrl}
      onError={revokeImageUrl}
    />
  );
}

/**
 * A gallery tile's picture of a file: the image itself for an image file, and
 * a file icon for anything else.
 */
export default function FileThumbnail({
  file,
  chatThread,
}: FileThumbnailProps) {
  const preview = getPreviewKind(file.filename);
  const permalink = findVariantPermalink(file.variants, "original");
  if (
    preview.kind !== "image" ||
    preview.mimeType === undefined ||
    permalink === null
  ) {
    return fileIcon;
  }
  return (
    <ImageThumbnail
      fileName={file.filename}
      chatThread={chatThread}
      permalink={permalink}
      mimeType={preview.mimeType}
    />
  );
}
